import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Visibility,
  Link,
  Search,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import QRCodeCreation from "./QrCodeCreation";
import { useTranslation } from "react-i18next";
import { qrCodeService } from "../../services/qrCodeService";
import { useSnackbar } from "notistack";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import { QRCode } from "react-qrcode-logo";
import DownloadIcon from "@mui/icons-material/Download";

const PREFIX = "QRCodeManagement";

const classes = {
  root: `${PREFIX}-root`,
  backdropStyle: `${PREFIX}-backdropStyle`,
  imageStyle: `${PREFIX}-imageStyle`,
  targetContainer: `${PREFIX}-targetContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  exparienceLogoContainer: `${PREFIX}-exparienceLogoContainer`,
  filtersContainer: `${PREFIX}-filtersContainer`,
};

const QRCodeManagementContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      width: "100%",
      maxWidth: "1280px",
      marginLeft: "auto",
      marginRight: "auto",
      padding: "0 24px 0 24px",
      [theme.breakpoints.between("xs", "sm")]: {
        padding: "12px",
      },
    },
    [`& .${classes.backdropStyle}`]: {
      color: "#D3AF5F",
      zIndex: "1000",
    },
    [`& .${classes.imageStyle}`]: {
      height: "65px",
      objectFit: "contain",
    },
    [`& .${classes.targetContainer}`]: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
    },
    [`& .${classes.exparienceLogoContainer}`]: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "10px",
    },
    [`& .${classes.filtersContainer}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  };
});

const QRCodeManagement = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [creationOpen, setCreationOpen] = useState({ open: false, id: null });
  const [loading, setLoading] = useState(false);
  const [listQRCodes, setListQrCodes] = useState([]);
  const [fullQrList, setFullQrList] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [filter, setFilter] = useState("");
  const [qrToDownload, setQrToDownload] = useState(null);

  const canvasRefs = useRef({});

  useEffect(() => {
    if (qrToDownload) {
      let canvas = canvasRefs.current[qrToDownload.id].canvas.current;
      let image = canvas.toDataURL({ format: "image/png", quality: 1 });
      let anchor = document.createElement("a");
      anchor.href = image;
      const finalName = qrToDownload.name.replaceAll(" ", "-");
      anchor.download = `QRCode-${finalName}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setQrToDownload(null);
    }
  }, [qrToDownload]);

  const downloadQR = (id, name, params) => {
    setQrToDownload({ id, name, params: params.row });
  };

  useEffect(() => {
    setLoading(true);
    qrCodeService
      .getQrCodes()
      .then((res) => {
        setListQrCodes(res);
        setFullQrList(res);
        setLoading(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("toasts.getQrCodeFailed"), { variant: "error" });
      });
  }, []);

  async function copyTextToClipboard(text) {
    return await navigator.clipboard.writeText(text);
  }

  const handleQrPreview = (target) => {
    window.open(target, "_blank", "noreferrer");
  };

  const handleDeleteQr = () => {
    setOpenBackdrop(true);
    const tmpQrList = [...listQRCodes];
    const indexToDelete = tmpQrList.findIndex(
      (qrList) => qrList._id === idToDelete
    );
    qrCodeService
      .deleteQrCode(idToDelete)
      .then(() => {
        tmpQrList.splice(indexToDelete, 1);
        setListQrCodes(tmpQrList);
        enqueueSnackbar(t("QrManager.toasts.deleteSuccess"), {
          variant: "success",
        });
        setOpenBackdrop(false);
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("QrManager.toasts.deleteFail"), {
            variant: "error",
          });
        setOpenBackdrop(false);
      });
  };

  const columns = [
    // { field: 'id', headerName: 'Id', width: 70, type: 'string', hide: true },
    {
      field: "QrCode",
      headerName: "QrCode",
      width: 190,
      renderCell: (params) => {
        return (
          <div key={params.row._id}>
            <QRCode
              id="react-qrcode-logo"
              value={params.row.shortLink}
              enableCORS={true}
              size={125}
              quietZone={params.row.qrParams.qrBorder}
              ecLevel={params.row.qrParams.ecLevel}
              bgColor={params.row.qrParams.backgroundColor} // black default
              fgColor={params.row.qrParams.dotsColor} //golden default
              includeMargin={true}
              qrStyle={params.row.qrParams.dotsStyle}
              logoImage={
                params.row.qrParams.logoUrl ? params.row.qrParams.logoUrl : null
              }
              logoHeight={
                params.row.qrParams.logoHeight
                  ? params.row.qrParams.logoHeight / 2
                  : null
              }
              logoWidth={
                params.row.qrParams.logoWidth
                  ? params.row.qrParams.logoWidth / 2
                  : null
              }
              logoPadding={params.row.qrParams.logoPadding}
              logoPaddingStyle={params.row.qrParams.logoPaddingStyle}
              eyeRadius={[
                params.row.qrParams.eyeTopLeftShape,
                params.row.qrParams.eyeTopRightShape,
                params.row.qrParams.eyeBottomLeftShape,
              ]}
              eyeColor={params.row.qrParams.eyeColor}
              logoOpacity={
                params.row.qrParams.logoOpacity
                  ? params.row.qrParams.logoOpacity
                  : null
              }
              removeQrCodeBehindLogo={params.row.qrParams.logoBackgroundEmpty}
            />
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => {
        return (
          <div>
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setCreationOpen({ open: true, id: params.row._id });
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleQrPreview(params.row.targetUrl)}
            >
              <Visibility />
            </IconButton>
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() =>
                downloadQR(params.row._id, params.row.name, params)
              }
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                copyTextToClipboard(params.row.targetUrl);
                enqueueSnackbar(t("toasts.copiedInClipboard"), {
                  variant: "info",
                });
              }}
            >
              <Link />
            </IconButton>
            <IconButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setIdToDelete(params.row._id);
                setOpenConfirmationDialog(true);
              }}
            >
              <Delete />
            </IconButton>
          </div>
        );
      },
    },
    { field: "totalCounter", headerName: "Scans", width: 70 },
    { field: "name", headerName: `${t("QrManager.name")}`, width: 150 },
    {
      field: "exparience",
      headerName: "Exparience",
      width: 250,
      renderCell: (params) => (
        <div className={classes.targetContainer}>
          {params.row.exparience && (
            <>
              <Typography variant="body1" className={classes.marginBottom}>
                {params.row.exparience.label}
              </Typography>
              <div className={classes.exparienceLogoContainer}>
                <img
                  src={params.row.exparience.imageUrl}
                  className={classes.imageStyle}
                  alt="exparience"
                />
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      field: "myDesign",
      headerName: "MyDesign",
      width: 250,
      renderCell: (params) => (
        <div className={classes.targetContainer}>
          {params.row.myDesign && (
            <>
              <Typography variant="body1" className={classes.marginBottom}>
                {params.row.myDesign.label}
              </Typography>
              <img
                src={params.row.myDesign.imageUrl}
                className={classes.imageStyle}
                alt="myDesign"
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const search = () => {
    const tmp = [...fullQrList].filter((elem) => {
      const myFilter = filter.toLowerCase();
      return (
        elem.exparience?.label.toLowerCase()?.includes(myFilter) ||
        elem.myDesign?.label.toLowerCase()?.includes(myFilter) ||
        elem.name.toLowerCase().includes(myFilter)
      );
    });
    setListQrCodes(tmp);
  };

  const reinit = () => {
    setListQrCodes(fullQrList);
  };

  if (!loading)
    return (
      <QRCodeManagementContainer>
        {qrToDownload && (
          <Box sx={{ display: "none" }}>
            <QRCode
              ref={(el) => (canvasRefs.current[qrToDownload.params._id] = el)}
              id="react-qrcode-logo"
              value={qrToDownload.params.shortLink}
              enableCORS={true}
              size={1250}
              quietZone={qrToDownload.params.qrParams.qrBorder * 5}
              ecLevel={qrToDownload.params.qrParams.ecLevel}
              bgColor={qrToDownload.params.qrParams.backgroundColor} // black default
              fgColor={qrToDownload.params.qrParams.dotsColor} //golden default
              includeMargin={true}
              qrStyle={qrToDownload.params.qrParams.dotsStyle}
              logoImage={
                qrToDownload.params.qrParams.logoUrl
                  ? qrToDownload.params.qrParams.logoUrl
                  : null
              }
              logoHeight={
                qrToDownload.params.qrParams.logoHeight
                  ? (qrToDownload.params.qrParams.logoHeight / 2) * 10
                  : null
              }
              logoWidth={
                qrToDownload.params.qrParams.logoWidth
                  ? (qrToDownload.params.qrParams.logoWidth / 2) * 10
                  : null
              }
              logoPadding={qrToDownload.params.qrParams.logoPadding}
              logoPaddingStyle={qrToDownload.params.qrParams.logoPaddingStyle}
              eyeRadius={[
                qrToDownload.params.qrParams.eyeTopLeftShape.map(
                  (item) => item * 10
                ),
                qrToDownload.params.qrParams.eyeTopRightShape.map(
                  (item) => item * 10
                ),
                qrToDownload.params.qrParams.eyeBottomLeftShape.map(
                  (item) => item * 10
                ),
              ]}
              eyeColor={qrToDownload.params.qrParams.eyeColor}
              logoOpacity={
                qrToDownload.params.qrParams.logoOpacity
                  ? qrToDownload.params.qrParams.logoOpacity
                  : null
              }
              removeQrCodeBehindLogo={
                qrToDownload.params.qrParams.logoBackgroundEmpty
              }
            />
          </Box>
        )}
        <ResponsiveDialogManager
          title={"QrCode Creation"}
          fullScreen
          open={creationOpen.open}
          onClose={() => {
            setCreationOpen({ open: false, id: null });
          }}
          renderer={
            <QRCodeCreation
              setListQrCodes={setListQrCodes}
              setCreationOpen={setCreationOpen}
              creationOpen={creationOpen}
            />
          }
        />
        <ConfirmationDialog
          open={openConfirmationDialog}
          element="QrCode"
          onAgree={() => handleDeleteQr()}
          setOpen={setOpenConfirmationDialog}
          mode="QrCodes"
        />
        <ConfirmationDialog
          open={openConfirmationDialog}
          element="QrCode"
          onAgree={() => handleDeleteQr()}
          setOpen={setOpenConfirmationDialog}
          mode="QrCodes"
        />
        <div className={classes.root}>
          <Backdrop className={classes.backdropStyle} open={openBackdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <h1>{t("QrManager.title")}</h1>
          <h4 className={classes.marginBottom}>{t("entities.filters")}</h4>
          <div className={classes.filtersContainer}>
            <div className={classes.filter}>
              <TextField
                variant="outlined"
                margin="normal"
                id="name"
                label={t("entities.name")}
                name="name"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className={classes.filterButtons}>
              <Button onClick={search} variant="contained" color="primary">
                <Search />
                {t("entities.buttons.search")}
              </Button>
              <Button
                onClick={reinit}
                variant="contained"
                sx={{
                  marginLeft: "1rem",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                {t("entities.buttons.init")}
              </Button>
            </div>
          </div>
          <div>
            <IconButton
              variant="contained"
              color="primary"
              onClick={() => setCreationOpen({ open: true, id: null })}
            >
              <Add />
            </IconButton>
          </div>
          <DataGrid
            sx={{
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "15px",
              },
            }}
            rows={listQRCodes}
            getRowId={(listQRCodes) => listQRCodes._id}
            columns={columns}
            autoHeight
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            pagination
            loading={loading}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
          />
        </div>
      </QRCodeManagementContainer>
    );
  else
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>{t("loaders.fetching")}</Typography>
      </div>
    );
};

export default QRCodeManagement;
